<template>
  <div class="internet-product-info">
    <div class="detail_menu">
      <div v-for="tab in tabs" class="menu_item" :class="{ 'active': activeTab === tab.id }"
        @click="activateTab(tab.id)">
        <icon :icon="tab.icon" />
        <span>{{ tab.label }}</span>
      </div>
    </div>

    <div class="detail_innercontent">
      <Transition :name="slideDirection">
        <component class="product-tab-content" :is="activeTabComponent" v-if="activeTabComponent" :key="activateTab.id"
          :product="product" />
      </Transition>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, watch, onMounted } from 'vue';
import Icon from 'vue-elements/icon/Icon.vue';
import PricingTab from './PricingTab.vue';
import InternetTab from './InternetTab.vue';
import TvTab from './TvTab.vue';

const props = defineProps({
  product: {
    type: Object,
    required: true,
  },
  activeTab: {
    type: String,
    default: 'pricing',
  },
  startTab: {
    type: String,
    default: '',
  },
});

const tabs = computed(() => {
  return [
    { id: 'internet', label: 'Internet', icon: 'x-fa-euro-sign' },
    { id: 'pricing', label: 'Prijs', icon: 'x-fa-leaf' },
    { id: 'tv', label: 'Televisie', icon: 'x-fa-lightbulb' },
  ];
});
const activeTabComponent = computed(() => {
  switch (activeTab.value) {
    case 'pricing':
      return PricingTab;
    case 'internet':
      return InternetTab;
    case 'tv':
      return TvTab;
  }
});

watch(() => props.startTab, (newTab) => {
  if (newTab) {
    activateTab(newTab);
  }
});
const activeTab = ref(props.activeTab);
const slideDirection = ref('tab-slide-right');

const activateTab = (tab) => {
  const currentIndex = tabs.value.findIndex(t => t.id === activeTab.value);
  const newIndex = tabs.value.findIndex(t => t.id === tab);
  slideDirection.value = newIndex < currentIndex ? 'tab-slide-left' : 'tab-slide-right';
  activeTab.value = tab;
}

onMounted(() => {
  if (props.startTab) {
    activateTab(props.startTab);
  } else {
    activateTab(activeTab.value);
  }
});

</script>

<style lang="scss">
@use 'assets/scss/_variables/colors';
@use 'assets/scss/_helpers/typography';
@use 'assets/scss/_helpers/display';
@use '../assets/scss/all' as *;

.detail_menu {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  border-top: 1px solid colors.$border-base;

  .menu_item {
    @include flex-col-center;

    gap: 8px;
    padding: 12px 4px;
    font-weight: 600;
    cursor: pointer;
    color: colors.$link-base-default;
    border-bottom: 1px solid colors.$border-base;

    @include breakpoint-up(md) {
      padding: 12px 16px;
    }


    .icon-element {
      background-color: colors.$link-base-default;
    }

    &:hover {
      color: colors.$link-base-hover;
      border-bottom: 2px solid colors.$link-base-hover;

      .icon-element {
        color: colors.$link-base-hover;
        background: colors.$link-base-hover;
      }
    }

    .icon-element {
      width: 16px;
      height: 16px;
    }

    &.active {
      font-weight: 500;
      border-bottom: 2px solid colors.$link-base-default;
    }
  }
}

.detail_innercontent {
  padding: 16px 0;

  @include breakpoint-up(lg) {
    padding: 32px;
  }

  .header {
    font-size: 1.1rem;
    padding-right: 20px;
    padding-top: 18px;
    font-weight: normal;
    vertical-align: top;
  }

  table {
    margin-left: 0;
    margin-top: 12px;
    width: auto;
    background: transparent;

    @include breakpoint-down(sm) {
      tr {
        flex-direction: column;
        width: 100%;
        display: flex;
      }
    }

    tr:not(:first-child) {
      border-top: 1px solid colors.$border-base;
    }

    .info_content {
      padding-top: 4px;
      padding-bottom: 4px;
    }
  }

  p {
    line-height: 1.3em;
  }

  a {
    text-decoration: underline;
    color: colors.$link-base-default;
    display: inline;
    cursor: pointer;
  }

  .prop {
    @include paragraph-14;
    padding: 8px 12px;
    font-weight: 600;
    border: 1px solid colors.$border-base;
    align-items: center;
    gap: 8px;
    display: inline-flex;
    margin: 4px;
    background-color: $grey-10;
    border-radius: 4px;
    color: $text-subtle;

    .icon-element {
      background-color: colors.$text-subtle;
    }
  }
}

.info_price {
  .pricing-table-row.list_panel {
    @include paragraph-15;
    padding: 4px 0;
    display: flex;
    justify-content: space-between;
    border-bottom: 1px solid $border-subtle;

    &.header {
      font-weight: 600;
      @include paragraph-17;
      border-bottom: 0;
      margin-top: 8px;

    }
  }

  .wli_list_panel>.wli_row {
    @include paragraph-15;
    display: flex;
    justify-content: space-between;
    border-bottom: 1px solid $border-subtle;
    padding: 5px 0;

    .icon-element {
      margin-right: 4px;
    }
  }
}


.internet-product-info {
  position: relative;
  overflow: hidden;
}


.tab-slide-right-enter-active,
.tab-slide-left-enter-active,
.tab-slide-right-leave-active,
.tab-slide-left-leave-active {
  transition: transform .35s cubic-bezier(.44, .08, .42, 1.06);
}

.tab-slide-right-leave-active,
.tab-slide-left-leave-active {
  position: absolute;
  width: 100%;
}

.tab-slide-left-leave-to,
.tab-slide-right-enter-from {
  transform: translateX(100%);
}

.tab-slide-left-enter-from {
  transform: translateX(-100%);
}

.tab-slide-right-leave-to {
  transform: translateX(-100%);
}

.tab-slide-left-leave-to {
  transform: translateX(100%);
}
</style>
